'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AudioLinesIcon,
  BellIcon,
  ChevronDownIcon,
  EarOffIcon,
  GaugeIcon,
  LockIcon,
  LogInIcon,
  LogOutIcon,
  MessageCircleMoreIcon,
  MicIcon,
  MicVocalIcon,
  PauseIcon,
  PlayIcon,
  Settings2Icon,
  SparklesIcon,
  ThermometerIcon,
  TimerIcon,
  Volume2Icon,
} from 'lucide-react';
import { toast } from 'sonner';

import type { components } from '@/api/v1/query/schema';
import { FormError } from '@/components/atoms/form-label';
import { MultiSelect } from '@/components/atoms/multi-select';
import { SuperUserOnly } from '@/components/providers/admin-only';
import { States } from '@/components/providers/states';
import { useApiV1Ctx } from '@/components/providers/v1-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { cn, hasErrors } from '@/lib/utils';

import { useVoiceSettingsCtx } from './provider';

type VoiceResponseItem = components['schemas']['VoiceResponseItem'];

export function VoiceAgentGeneral() {
  const {
    settings: { general },
    onChange: onSettingsChange,
    errors,
  } = useVoiceSettingsCtx();

  const onChange = (v: Partial<typeof general>) => {
    onSettingsChange({ general: { ...general, ...v } });
  };

  const languageOptions = useMemo(
    () => [
      { value: 'English', label: 'English' },
      { value: 'Spanish', label: 'Spanish' },
    ],
    []
  );

  const selectedLanguages = useMemo(() => {
    const value = general.language ?? 'English';
    if (value === 'English and Spanish') return ['English', 'Spanish'];
    if (value === 'Spanish') return ['Spanish'];
    return ['English'];
  }, [general]);

  const handleLanguageChange = (values: string[]) => {
    let language: string;
    const hasEnglish = values.includes('English');
    const hasSpanish = values.includes('Spanish');
    if (hasEnglish && hasSpanish) language = 'English and Spanish';
    else if (hasSpanish) language = 'Spanish';
    else language = 'English';
    onChange({ language });
  };

  return (
    <div className='flex w-full flex-col gap-4'>
      <Card>
        <CardHeader>
          <div className='flex items-center gap-2'>
            <SparklesIcon className='size-4' />
            <CardTitle>Agent Name & Voice</CardTitle>
          </div>
          <CardDescription>Give your voice agent a name and select their voice.</CardDescription>
        </CardHeader>
        <CardContent className='flex flex-row gap-4'>
          <div className='w-full space-y-1.5'>
            <Label className='px-1'>Name</Label>
            <Input
              value={general.agent_name}
              onChange={(e) => onChange({ agent_name: e.target.value })}
              aria-invalid={hasErrors(errors?.general?.agent_name?._errors)}
            />
            <FormError errors={errors?.general?.agent_name?._errors} className='mt-1' />
          </div>
          <div className='w-full space-y-1.5'>
            <Label className='px-1'>Voice</Label>
            <VoiceSelectDialog />
          </div>
        </CardContent>
        <CardContent className='flex flex-row gap-4'>
          <div className='w-full space-y-1.5'>
            <Label className='px-1'>Language</Label>
            <MultiSelect
              label='Select languages'
              selected={selectedLanguages}
              options={languageOptions}
              onChange={handleLanguageChange}
              className='w-full justify-start'
            />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <div className='flex items-center gap-2'>
            <LogInIcon className='size-4' />
            <CardTitle>Intro Message</CardTitle>
          </div>
        </CardHeader>
        <CardContent className='flex flex-col gap-4'>
          <div className='flex flex-col gap-1.5'>
            <Label>Office Hours Intro Message</Label>
            <Textarea
              value={general.intro_in_hours}
              onChange={(e) => onChange({ intro_in_hours: e.target.value })}
              aria-invalid={hasErrors(errors?.general?.intro_in_hours?._errors)}
            />
            <FormError errors={errors?.general?.intro_in_hours?._errors} />
          </div>
          <div className='flex flex-col gap-1.5'>
            <Label>After Hours Intro Message</Label>
            <Textarea
              value={general.intro_after_hours}
              onChange={(e) => onChange({ intro_after_hours: e.target.value })}
              aria-invalid={hasErrors(errors?.general?.intro_after_hours?._errors)}
            />
            <FormError errors={errors?.general?.intro_after_hours?._errors} />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <div className='flex items-center gap-2'>
            <LogOutIcon className='size-4' />
            <CardTitle>Outro Message</CardTitle>
          </div>
          <CardDescription>What the agent says when ending a call.</CardDescription>
        </CardHeader>
        <CardContent className='flex flex-col gap-4'>
          <Textarea value={general.outro} onChange={(e) => onChange({ outro: e.target.value })} />
        </CardContent>
      </Card>
      <SuperUserOnly>
        <Card>
          <CardHeader className='flex flex-row items-center justify-between gap-2'>
            <div className='flex items-center gap-2'>
              <LockIcon className='size-4' />
              <CardTitle>Use legacy tools</CardTitle>
            </div>
            <Switch
              checked={general.use_legacy_tools}
              onCheckedChange={(checked) => onChange({ use_legacy_tools: checked })}
            />
          </CardHeader>
        </Card>
        <Card>
          <CardHeader>
            <div className='flex items-center gap-2'>
              <LockIcon className='size-4' />
              <CardTitle>Internal Instructions</CardTitle>
            </div>
            <CardDescription>Internal instructions. Only visible to Solea.</CardDescription>
          </CardHeader>
          <CardContent className='flex flex-col gap-4'>
            <Textarea
              value={general.internal}
              onChange={(e) => onChange({ internal: e.target.value })}
              className='min-h-[100px]'
            />
          </CardContent>
        </Card>
      </SuperUserOnly>
      <AdvancedSettings />
    </div>
  );
}

function VoiceSelectDialog() {
  const [open, setOpen] = useState(false);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const { api } = useApiV1Ctx();
  const {
    settings: { agent },
    onChange: onSettingsChange,
  } = useVoiceSettingsCtx();
  const { data, isLoading } = api.agentVoice.listVoices.useQuery(undefined, {
    enabled: open,
  });
  const voiceName = useMemo(() => {
    return agent?.voice_id?.split('-').pop();
  }, [agent?.voice_id]);

  // Clean up audio when dialog closes
  useEffect(() => {
    if (!open) {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      setCurrentlyPlaying(null);
    }
  }, [open]);

  const handlePlayPause = (voice: VoiceResponseItem) => {
    if (!voice.preview_audio_url) {
      toast.error("Sorry, this voice can't be played.");
      return;
    }
    // If this voice is currently playing, pause it
    if (currentlyPlaying === voice.voice_id) {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      setCurrentlyPlaying(null);
      return;
    }

    // Stop any currently playing audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
    }

    // Play new audio
    const audio = new Audio(voice.preview_audio_url);
    currentAudioRef.current = audio;
    setCurrentlyPlaying(voice.voice_id);

    audio.addEventListener('ended', () => {
      setCurrentlyPlaying(null);
      currentAudioRef.current = null;
    });

    audio.play();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant='outline' className='w-full justify-between'>
          {voiceName}
          <ChevronDownIcon className='size-4' />
        </Button>
      </DialogTrigger>
      <DialogContent showClose className='flex h-[90vh] w-full flex-col gap-4 overflow-y-auto md:max-w-3xl'>
        <DialogHeader>
          <DialogTitle>Select Voice</DialogTitle>
          <DialogDescription>
            Select a voice for your agent. Each voice has different traits - age, gender, accent.
          </DialogDescription>
        </DialogHeader>
        <States.Provider isLoading={isLoading} isEmpty={!data?.length}>
          <States.Loading>
            <Skeleton className='h-full w-full' />
          </States.Loading>
          <States.Empty>
            <div className='flex w-full flex-col gap-4'>
              <p>No voices found</p>
            </div>
          </States.Empty>
          <States.Default>
            <div className='flex w-full flex-col gap-4'>
              {data?.map((voice) => (
                <VoiceItemCard
                  key={voice.voice_id}
                  voice={voice}
                  isPlaying={currentlyPlaying === voice.voice_id}
                  onPlayPause={handlePlayPause}
                  onSelect={(voice) => {
                    onSettingsChange({ agent: { ...agent, voice_id: voice.voice_id } });
                    setOpen(false);
                  }}
                />
              ))}
            </div>
          </States.Default>
        </States.Provider>
      </DialogContent>
    </Dialog>
  );
}

interface VoiceItemCardProps {
  voice: VoiceResponseItem;
  isPlaying: boolean;
  onPlayPause: (voice: VoiceResponseItem) => void;
  onSelect: (voice: VoiceResponseItem) => void;
}

function VoiceItemCard({ voice, isPlaying, onPlayPause, onSelect }: VoiceItemCardProps) {
  return (
    <div className='bg-background flex w-full items-center justify-between gap-4 rounded-lg border p-3'>
      <div className='space-y-1'>
        <div className='font-semibold'>{voice.voice_name}</div>
        <div className='flex items-center gap-2'>
          {voice.age && <Badge variant='outline'>{voice.age}</Badge>}
          {voice.gender && <Badge variant='outline'>{voice.gender}</Badge>}
          {voice.accent && <Badge variant='outline'>{voice.accent}</Badge>}
        </div>
      </div>
      <div className='flex items-center gap-2'>
        <Button variant='outline' onClick={() => onPlayPause(voice)}>
          {isPlaying ? <PauseIcon className='size-4' /> : <PlayIcon className='size-4' />}
        </Button>
        <Button variant='outline' onClick={() => onSelect(voice)}>
          Select
        </Button>
      </div>
    </div>
  );
}

function AdvancedSettings() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Card className='gap-3 py-0'>
      <CardHeader
        className='hover:bg-muted/50 flex cursor-pointer items-center gap-2 py-6'
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className='flex items-center gap-2'>
          <Settings2Icon className='size-4' />
          <CardTitle>Advanced Settings</CardTitle>
        </div>
        <ChevronDownIcon className={cn('ml-auto size-4 transition-transform', isOpen && 'rotate-180')} />
      </CardHeader>
      {isOpen && (
        <CardContent className='mt-0 flex flex-col gap-4 pb-6'>
          <AdvancedSettingsContent />
        </CardContent>
      )}
    </Card>
  );
}

function AdvancedSettingsContent() {
  const {
    settings: { agent },
    onChange: onSettingsChange,
  } = useVoiceSettingsCtx();

  return (
    <>
      <div className='bg-muted flex w-full flex-col gap-4 rounded-lg border p-4'>
        <div className='flex items-center gap-2'>
          <MicIcon className='size-4' />
          <div className='text-sm font-semibold'>Voice Characteristics</div>
        </div>
        <div className='bg-background flex flex-col gap-2 rounded-lg p-4'>
          <div className='flex items-center gap-2'>
            <GaugeIcon className='size-4' />
            <Label>Speed</Label>
          </div>
          <div className='flex items-center gap-3'>
            <Slider
              min={0.5}
              max={2}
              step={0.1}
              value={[agent.voice_speed]}
              onValueChange={(value) => onSettingsChange({ agent: { ...agent, voice_speed: value[0] } })}
              className='w-full'
            />
            <div className='text-sm font-semibold'>{agent.voice_speed.toFixed(1)}x</div>
          </div>
        </div>
        <div className='bg-background flex flex-col gap-2 rounded-lg p-4'>
          <div className='flex items-center gap-2'>
            <Volume2Icon className='size-4' />
            <Label>Volume</Label>
          </div>
          <div className='flex items-center gap-3'>
            <Slider
              min={0}
              max={2}
              step={0.1}
              value={[agent.volume]}
              onValueChange={(value) => onSettingsChange({ agent: { ...agent, volume: value[0] } })}
              className='w-full'
            />
            <div className='text-sm font-semibold'>{Math.round(agent.volume * 100)}%</div>
          </div>
        </div>
        <div className='bg-background flex flex-col gap-2 rounded-lg p-4'>
          <div className='flex items-center gap-2'>
            <ThermometerIcon className='size-4' />
            <Label>Temperature</Label>
          </div>
          <div className='flex items-center gap-3'>
            <Slider
              min={0}
              max={2}
              step={0.1}
              value={[agent.voice_temperature]}
              onValueChange={(value) => onSettingsChange({ agent: { ...agent, voice_temperature: value[0] } })}
              className='w-full'
            />
            <div className='text-sm font-semibold'>{agent.voice_temperature.toFixed(1)}</div>
          </div>
          <div className='text-muted-foreground text-sm'>Controls voice randomness. Lower is more deterministic.</div>
        </div>
      </div>
      <div className='bg-muted flex w-full flex-col gap-4 rounded-lg border p-4'>
        <div className='flex items-center gap-2'>
          <AudioLinesIcon className='size-4' />
          <div className='text-sm font-semibold'>Conversation Dynamics</div>
        </div>
        <div className='bg-background flex flex-col gap-2 rounded-lg p-4'>
          <div className='flex items-center gap-2'>
            <MessageCircleMoreIcon className='size-4' />
            <Label>Agent Responsiveness</Label>
          </div>
          <div className='flex items-center gap-3'>
            <Slider
              min={0}
              max={1}
              step={0.1}
              value={[agent.responsiveness]}
              onValueChange={(value) => onSettingsChange({ agent: { ...agent, responsiveness: value[0] } })}
              className='w-full'
            />
            <div className='text-sm font-semibold'>{agent.responsiveness.toFixed(1)}</div>
          </div>
          <div className='text-muted-foreground text-sm'>
            Lower value means less responsive agent (wait more, respond slower), higher value means faster exchanges
            (respond when it can).
          </div>
        </div>
        <div className='bg-background flex flex-col gap-2 rounded-lg p-4'>
          <div className='flex items-center gap-2'>
            <MicVocalIcon className='size-4' />
            <Label>Interruption Sensitivity</Label>
          </div>
          <div className='flex items-center gap-3'>
            <Slider
              min={0}
              max={1}
              step={0.1}
              value={[agent.interruption_sensitivity]}
              onValueChange={(value) => onSettingsChange({ agent: { ...agent, interruption_sensitivity: value[0] } })}
              className='w-full'
            />
            <div className='text-sm font-semibold'>{agent.interruption_sensitivity.toFixed(1)}</div>
          </div>
          <div className='text-muted-foreground text-sm'>
            Lower value means it will take longer / more words for user to interrupt agent, while higher value means
            it&apos;s easier for user to interrupt agent.
          </div>
        </div>
        <ReminderSettings />
        <div className='bg-background flex flex-col gap-2 rounded-lg p-4'>
          <div className='flex items-center gap-2'>
            <EarOffIcon className='size-4' />
            <Label>End Call after Silence</Label>
          </div>
          <div className='flex items-center gap-3'>
            <Slider
              min={10000}
              max={600000}
              step={5000}
              value={[agent.end_call_after_silence_ms]}
              onValueChange={(value) => onSettingsChange({ agent: { ...agent, end_call_after_silence_ms: value[0] } })}
              className='w-full'
            />
            <div className='w-28 text-center text-sm font-semibold'>
              {Math.round(agent.end_call_after_silence_ms / 1000)}s (
              {(agent.end_call_after_silence_ms / 1000 / 60).toFixed(1)}min)
            </div>
          </div>
        </div>
        <div className='bg-background flex flex-col gap-2 rounded-lg p-4'>
          <div className='flex items-center gap-2'>
            <TimerIcon className='size-4' />
            <Label>Maximum Call Duration</Label>
          </div>
          <div className='flex items-center gap-3'>
            <Slider
              min={60000}
              max={3600000}
              step={60000}
              value={[agent.max_call_duration_ms]}
              onValueChange={(value) => onSettingsChange({ agent: { ...agent, max_call_duration_ms: value[0] } })}
              className='w-full'
            />
            <div className='w-28 text-center text-sm font-semibold'>
              {(agent.max_call_duration_ms / 1000 / 60).toFixed(1)}min
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function ReminderSettings() {
  const {
    settings: { agent },
    errors,
    onChange: onSettingsChange,
  } = useVoiceSettingsCtx();

  return (
    <div className='bg-background rounded-lg p-4'>
      <div className='space-y-0.5'>
        <div className='flex items-center gap-2'>
          <BellIcon className='size-4' />
          <div className='text-sm font-semibold'>Reminder</div>
        </div>
        <div className='text-muted-foreground text-sm'>
          Trigger a reminder for the customer to speak, if they have been silent for the specified duration.
        </div>
      </div>
      <div className='mt-4 grid grid-cols-2 gap-4'>
        <div className='border-border/50 bg-background flex w-full flex-col rounded-lg border p-3 shadow'>
          <Label>Reminder Count</Label>
          <div className='text-muted-foreground mt-1 text-sm'>Maximum number of reminders to trigger.</div>
          <Input
            className='mt-1.5'
            value={agent.reminder_max_count}
            onChange={(e) => {
              const value = parseInt(e.target.value);
              if (isNaN(value)) {
                return;
              }
              onSettingsChange({ agent: { ...agent, reminder_max_count: value } });
            }}
            type='number'
            min={0}
            max={10}
            aria-invalid={hasErrors(errors?.agent?.reminder_max_count?._errors)}
          />
          <FormError errors={errors?.agent?.reminder_max_count?._errors} className='mt-1' />
        </div>
        <div className='border-border/50 bg-background flex w-full flex-col rounded-lg border p-3 shadow'>
          <Label>Reminder Delay</Label>
          <div className='text-muted-foreground mt-1 text-sm'>
            Duration of silence after which a reminder will be triggered.
          </div>
          <div className='mt-1.5 flex h-9 items-center gap-3'>
            <Slider
              min={1000}
              max={60000}
              step={1000}
              value={[agent.reminder_trigger_ms]}
              onValueChange={(value) => onSettingsChange({ agent: { ...agent, reminder_trigger_ms: value[0] } })}
              className='w-full'
            />
            <div className='w-8 text-center text-sm font-semibold'>
              {(agent.reminder_trigger_ms / 1000).toFixed(1)}s
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
